/** Copyright (c) 2019 T.Rowe Price. All rights reserved. */

import './AccountDisplay.scss';

import classnames from 'classnames';
import { bool, shape, string } from 'prop-types';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { isBrokerage, isActiveAdvisory, isActivePlus } from 'services/Products';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import BrokerageLink from 'components/shared/Table/BrokerageLink';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';

const AccountNumber = ({ accountWChecksum, className }) => (
  <span className={className}>{accountWChecksum}</span>
);

const Owners = ({ owners, className }) => (
  <span className={className}>{owners}</span>
);

const AccountNumberWithPermissions = WithUserPermissions(AccountNumber);
const AccountOwnersWithPermissions = WithUserPermissions(Owners);

const AccountDisplay = ({
  account,
  className,
  withLink,
  withOwners,
  withType,
  includePending,
  showAccountLabel = true
}) => {
  const productType = account.sourceAccountDescriptor
    ? account.sourceAccountDescriptor.productType
    : account.productType;
  const forBrokerage = isBrokerage(productType);
  const forActiveAdvisory = isActiveAdvisory(productType);
  const showTicker = !isActivePlus(productType) && account.ticker;
  return (
    <div className={classnames('account-display', className)}>
      <div className="account-info">
        {withLink && forBrokerage && (
          <BrokerageLink
            accountName={account.accountName}
            accountId={account.accountId}
            className="account-name-link"
          />
        )}

        {withLink && !forBrokerage && !forActiveAdvisory && (
          <Link
            className="account-name-link"
            id={`account-name-link-${account.accountId}`}
            to={`/account-details?id=${account.accountId}`}>
            {account.accountName}
          </Link>
        )}

        {forActiveAdvisory && (
          <span className="account-name">{account.accountName}</span>
        )}

        {!withLink && !forActiveAdvisory && (
          <span className="account-name">{account.accountName}</span>
        )}

        {showTicker && <span className="account-ticker">{account.ticker}</span>}
        {includePending && <span className="pending-identifier">Pending</span>}
        {withType && withOwners && (
          <AccountNumberWithPermissions
            accountWChecksum={account.accountWChecksum}
            className="account-number"
            shouldMask
          />
        )}
      </div>
      <div>
        {withType && withOwners && (
          <Fragment>
            <span className="account-type">{account.accountType}</span>
            <span className="spacing-dot" />
            <AccountOwnersWithPermissions
              className="account-owners"
              owners={account.owners}
              shouldMask
            />
          </Fragment>
        )}
        {withType && !withOwners && (
          <Fragment>
            <span className="account-type">{account.accountType}</span>
            <span className="spacing-dot" />
            <AccountNumberWithPermissions
              accountWChecksum={account.accountWChecksum}
              className="account-number no-margin"
              shouldMask
            />
          </Fragment>
        )}
        {!withType && !withOwners && (
          <AccountNumberWithPermissions
            accountWChecksum={account.accountWChecksum}
            className="account-number no-margin"
            shouldMask
          />
        )}
        {!withType && withOwners && (
          <Fragment>
            <AccountNumberWithPermissions
              accountWChecksum={account.accountWChecksum}
              className="account-number no-margin"
              shouldMask
            />
            <span className="spacing-dot" />
            <AccountOwnersWithPermissions
              className="account-owners"
              owners={account.owners}
              shouldMask
            />
          </Fragment>
        )}
        <AccountLabel showAccountLabel={showAccountLabel}
                      accountId={account.accountId}/>
      </div>
    </div>
  );
};

AccountDisplay.propTypes = {
  account: shape({
    accountName: string,
    accountType: string,
    accountId: string.isRequired,
    accountWChecksum: string.isRequired,
    owners: string.isRequired
  }),
  className: string.isRequired,
  withLink: bool.isRequired,
  withOwners: bool.isRequired,
  withType: bool.isRequired
};

AccountDisplay.defaultProps = {
  className: '',
  withLink: true,
  withOwners: true,
  withType: false
};

export default AccountDisplay;
